import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";

import { del, HTTPError, patch, post } from "../helpers/HTTP";
import getQueryKeys from "./getQueryKeys";
import useWorkspaceCacheUpdate from "./useWorkspaceCacheUpdate";

import type {
  DetailedWorkspace,
  RentedSubscription,
  Subscription,
  SubscriptionSettings,
  SubscriptionType,
  WorkspaceAccount,
  WorkspaceLicense,
} from "../types";

async function createSubscriptionRequest(
  workspaceId: string,
  planId: string,
  count: number,
  type: SubscriptionType,
  paymentSourceId: string,
): Promise<Subscription | RentedSubscription> {
  return (
    await post(`workspaces/${workspaceId}/subscriptions`, {
      item_price_id: planId,
      item_count: count,
      type,
      payment_source_id: paymentSourceId,
    })
  ).subscription;
}

async function addCouponRequest(
  subscriptionId: string,
  coupon: string,
): Promise<SubscriptionSettings> {
  return (
    await patch(`subscriptions/${subscriptionId}/coupon`, {
      coupon,
    })
  ).subscription;
}

async function removeChangeRequest(
  workspaceId: string,
  subscriptionId: string,
): Promise<{ workspace: DetailedWorkspace; accounts: WorkspaceAccount[] }> {
  const {
    workspace: { accounts, ...detailedWorkspace },
  } = await del(
    `workspaces/${workspaceId}/subscriptions/${subscriptionId}/changes`,
  );

  return { workspace: detailedWorkspace, accounts };
}

export default function useSubscriptionMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { updateCachedWorkspace, updateCachedWorkspaceAccounts } =
    useWorkspaceCacheUpdate();

  const { mutateAsync: createSubscription, isPending: isCreatingSubscription } =
    useMutation({
      mutationFn: ({
        planId,
        count,
        type,
        paymentSourceId,
      }: {
        planId: string;
        count: number;
        type: SubscriptionType;
        paymentSourceId: string;
      }) =>
        createSubscriptionRequest(
          workspaceId,
          planId,
          count,
          type,
          paymentSourceId,
        ),
      onSuccess: (subscription, { count, type }) => {
        toast.success("Subscription created");

        updateCachedWorkspace(workspaceId, (draftWorkspace) => {
          if (type === "user") {
            draftWorkspace.subscription = subscription as Subscription;
            draftWorkspace.license_count += count;

            if (draftWorkspace.licenses) {
              // Generate new licenses
              const newLicenses: WorkspaceLicense[] = Array.from(
                { length: count },
                () => ({
                  status: "active",
                  lifetime: false,
                  expires_at: 0,
                  source: "aimfox",
                }),
              );

              draftWorkspace.licenses.push(...newLicenses);
            }
          } else {
            draftWorkspace.rental_subscription =
              subscription as RentedSubscription;
          }
        });
      },
      onError: (error) =>
        toast.error(
          error instanceof HTTPError && error.serverMessage
            ? error.serverMessage
            : "Failed to create subscription",
        ),
    });

  const { mutateAsync: addCoupon, isPending: isAddingCoupon } = useMutation({
    mutationFn: ({
      subscriptionId,
      coupon,
    }: {
      subscriptionId: string;
      coupon: string;
    }) => addCouponRequest(subscriptionId, coupon),
    onSuccess: (subscriptionSettings) => {
      toast.success("Coupon added");

      queryClient.setQueryData(
        ["subscriptionSettings", subscriptionSettings.id],
        subscriptionSettings,
      );
    },
    onError: () => toast.error("Failed to add coupon"),
  });

  const { mutateAsync: removeChange, isPending: isRemovingChange } =
    useMutation({
      mutationFn: ({
        subscription,
      }: {
        subscription: Subscription | RentedSubscription;
      }) => removeChangeRequest(workspaceId, subscription.id),
      onSuccess: ({ workspace, accounts }, { subscription }) => {
        updateCachedWorkspace(workspaceId, () => {
          return workspace;
        });

        updateCachedWorkspaceAccounts(workspaceId, () => {
          return accounts;
        });

        queryClient.invalidateQueries({
          queryKey: getQueryKeys(workspaceId).estimate.renew(subscription),
        });

        toast.success("Scheduled change removed");
      },
      onError: () => toast.error("Failed to remove scheduled change"),
    });

  return {
    createSubscription,
    isCreatingSubscription,

    removeChange,
    isRemovingChange,

    addCoupon,
    isAddingCoupon,
  };
}
